import { useEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { useTrail } from './TrailProvider';
import { getBaseRoute } from './LoginCard';
import { useFirebase } from './FirebaseContextProvider';

function getIdxSafe() {
  // React Router v6 stores a per-entry history index in window.history.state.idx
  const st = window.history && window.history.state;
  return st && typeof st.idx === 'number' ? st.idx : 0;
}

// Use pathname only (ignore search/hash) and normalize trailing slashes
function normalizePath(loc) {
  return loc.pathname.replace(/\/+$/, '') || '/';
}

export default function NavigationObserver() {
  const loc = useLocation();
  const navType = useNavigationType(); // 'PUSH' | 'REPLACE' | 'POP'
  const { actions, timeline } = useTrail();
  const { user } = useFirebase();

  useEffect(() => {
    const idx = getIdxSafe();
    const path = normalizePath(loc);

    // 1) Root landing: start a new clean session trail
    //    Use the normalized path for comparison.
    const baseRoute = getBaseRoute(user);
    if (baseRoute === path) {
      actions.resetTo(idx, path);
      return;
    }

    // 2) First load / direct URL / F5:
    //    If the timeline is empty and we got a POP (RR's initial navigation),
    //    seed the trail with the current node so "Home / ..." starts from here.
    if (navType === 'POP' && timeline.length === 0) {
      actions.resetTo(idx, path);
      return;
    }

    // 3) Regular navigation handling:
    //    - PUSH: append (and cut any "future" branch if we were after a Back)
    //    - REPLACE: mutate the current entry in place
    //    - POP: move the cursor only (do not mutate the timeline)
    if (navType === 'PUSH') {
      actions.push(idx, path);
    } else if (navType === 'REPLACE') {
      actions.replace(idx, path);
    } else if (navType === 'POP') {
      actions.popTo(idx);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.key]);

  return null; // observer renders nothing
}
